import {
  Autocomplete,
  Box,
  BoxProps,
  Skeleton,
  Stack,
  TextField,
} from "@mui/material";
import * as React from "react";
import {
  ReservesCourseOption,
  ReservesDepartmentOption,
  ReservesInstructorOption,
} from "./ReservesSearchForm";
import { useBranchLimits } from "./BranchLimits";
import { useLanguage } from "./Language";
import axios from "axios";
import { useReservesSearch } from "./pages/search/Reserves/ReservesSearchProvider";

type ReservesOption =
  | ReservesCourseOption
  | ReservesDepartmentOption
  | ReservesInstructorOption;

// TODO can we extend AutocompleteProps interface from MUI?
interface ReservesFilterAutocompleteProps<TOption extends ReservesOption> {
  label: string;
  loaded: boolean;
  value: string;
  options: TOption[];
  onChange: (value: string | null) => void;
  getOptionLabel: (value: TOption) => string;
}
export const ReservesFilterAutocomplete = <TOption extends ReservesOption>({
  label,
  loaded,
  value,
  onChange,
  options,
  ...props
}: ReservesFilterAutocompleteProps<TOption>) => {
  const { translate } = useLanguage();
  const selectedOption = React.useMemo(() => {
    if (!loaded) {
      return null;
    }
    if (!value) {
      return null;
    }
    if (options) {
      const match = options.find((option) => option.value === value);
      if (!match) {
        return null;
      }
      return match;
    }

    return null;
  }, [options, value, loaded]);

  return (
    <>
      {(loaded || selectedOption) && (
        <Autocomplete
          {...props}
          noOptionsText={translate("noOptions")}
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value;
          }}
          value={selectedOption}
          onChange={(event, newValue: ReservesOption | null, reason) => {
            if (reason === "clear") {
              onChange(null);
            } else {
              onChange(newValue?.value || null);
            }
          }}
          options={options}
          renderInput={(params) => <TextField {...params} label={label} />}
        />
      )}
      {!loaded && !selectedOption && <Skeleton height={"56px"}></Skeleton>}
    </>
  );
};

export interface ReservesFilterControlProps extends BoxProps {
  instructor: ReservesInstructorOption["value"] | null;
  course: ReservesCourseOption["value"] | null;
  department: ReservesDepartmentOption["value"] | null;
  onChangeCourse: (event: ReservesCourseOption["value"] | null) => void;
  onChangeDepartment: (event: ReservesDepartmentOption["value"] | null) => void;
  onChangeInstructor: (event: ReservesInstructorOption["value"] | null) => void;
}

// the parent should control the state of instructor, department, and course
export const ReservesFilterControl: React.FC<ReservesFilterControlProps> = ({
  onChangeCourse,
  onChangeDepartment,
  onChangeInstructor,
  instructor,
  department,
  course,
  ...props
}) => {
  const { translate } = useLanguage();

  const { options, allOptions, loadingOptions } = useReservesSearch();

  const extendOptionsWithValue = <T extends { value: string }>(
    value: string,
    filteredOptions: T[] = [],
    allOptions: T[] = []
  ): T[] => {
    if (!value || filteredOptions.some((option) => option.value === value)) {
      return filteredOptions;
    }

    const match = allOptions.find((option) => option.value === value);
    return match ? [match, ...filteredOptions] : filteredOptions;
  };

  const useExtendedOptions = <T extends { value: string }>(
    value: string,
    filteredOptions?: T[],
    unfilteredOptions?: T[]
  ) =>
    React.useMemo(() => {
      if (!filteredOptions || !unfilteredOptions) {
        return [];
      }
      return extendOptionsWithValue(value, filteredOptions, unfilteredOptions);
    }, [value, filteredOptions, unfilteredOptions]);

  const instructorOptions = useExtendedOptions(
    instructor,
    options?.instructors,
    allOptions?.instructors
  );

  const courseOptions = useExtendedOptions(
    course,
    options?.courses,
    allOptions?.courses
  );

  const departmentOptions = useExtendedOptions(
    department,
    options?.departments,
    allOptions?.departments
  );

  return (
    <Box {...props}>
      <Stack sx={{ gap: 2, maxWidth: "320px" }}>
        <ReservesFilterAutocomplete<ReservesDepartmentOption>
          label={translate("departments")}
          loaded={!loadingOptions}
          value={department}
          options={departmentOptions}
          onChange={(value) => onChangeDepartment(value)}
          getOptionLabel={(option) => `${option.name} - ${option.comment}`}
        ></ReservesFilterAutocomplete>
        <ReservesFilterAutocomplete<ReservesCourseOption>
          label={translate("courses")}
          loaded={!loadingOptions}
          value={course}
          options={courseOptions}
          onChange={(value) => onChangeCourse(value)}
          getOptionLabel={(option) => `${option.name} - ${option.comment}`}
        ></ReservesFilterAutocomplete>
        <ReservesFilterAutocomplete<ReservesInstructorOption>
          label={translate("instructors")}
          loaded={!loadingOptions}
          value={instructor}
          options={instructorOptions}
          onChange={(value) => onChangeInstructor(value)}
          getOptionLabel={(option) => `${option.name}`}
        ></ReservesFilterAutocomplete>
      </Stack>
    </Box>
  );
};
